import { forwardRef, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';

import { Timetable } from '../Timetable/Timetable';
import type { TimetableProps } from '../Timetable/Timetable';
import { TimetableItem } from '../TimetableItem/TimetableItem';
import { getTimetable } from '../../services/liverpool';
import { useCurrentTime } from '../utils/useCurrentTime';
import {
  createTimetableFromProgramme,
  getTimetableLastDay,
} from '../Timetable/helpers';

export const LIVERPOOL_FC_ASSET_ID = 222463;
export const LIVERPOOL_FC_SCHEDULE_URL =
  'https://vgc.no/vglive/liverpool/schedule.json';

type LiverpoolTimetableProps = TimetableProps;

export const LiverpoolTimetable = forwardRef<
  HTMLDivElement,
  LiverpoolTimetableProps
>((props, ref) => {
  const today = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  const { currentTime } = useCurrentTime();

  const [isLoading, setIsLoading] = useState(true);
  const [date, setDate] = useState(() => currentTime.format('YYYY-MM-DD'));
  const [timetable, setTimetable] = useState<
    ReturnType<typeof createTimetableFromProgramme>
  >({});

  const currentTimetable = useMemo(
    () => timetable[date] ?? [],
    [timetable, date]
  );

  const timetableLastDay = useMemo(
    () => getTimetableLastDay(timetable),
    [timetable]
  );

  useEffect(() => {
    const controller = new AbortController();

    getTimetable({ signal: controller.signal })
      .then((response) => {
        setTimetable(createTimetableFromProgramme(response.schedule.programme));

        setIsLoading(false);
      })
      .catch((err) => {
        if (err.name === 'AbortError') {
          return;
        }

        console.error(err);
      });

    return () => controller.abort();
  }, []);

  return isLoading ? null : (
    <Timetable
      date={date}
      minDate={today}
      maxDate={timetableLastDay}
      onChange={setDate}
      {...props}
      ref={ref}
    >
      {currentTimetable
        .filter((e) => dayjs(e.endTime) >= currentTime)
        .map((e) => (
          <TimetableItem
            time={e.time}
            isCurrentlyLive={currentTime.isBetween(
              e.startTime,
              e.endTime,
              null,
              '[]'
            )}
            title={e.title}
            key={`${e.title}-${e.startTime}-${e.endTime}`}
            description={e.description}
          />
        ))}
    </Timetable>
  );
});
